import React, {useEffect, useState} from 'react'
import {Button, Icon, Loading, Pagination, toastManager} from '@therms/atalaya'
import {
  Alerts,
  type DownloadPdfResult,
  DownloadLogEntriesModal,
  PickLocationLogEntryRefsModal,
  RPCDocs,
  RPCs,
  useCurrentUserAnPCheck,
  useDownloadsApiServiceContext,
  useLocalStorage,
  useLocationContext,
  useLocationLogEntriesByDate,
  usePaginationState,
  useRPC,
  useRPCServerEventHandler,
  useUrlState,
  useUrlStateDebounced,
} from '@therms/react-modules'
import {EntryFormModal} from './components/EntryFormModal'
import {downloadBase64AsPdf, pluralize} from '@therms/web-js'
import dayjs from 'dayjs'
import {SelectLocationLogEntryTypeModal} from './components/SelectLocationLogEntryTypeModal'
import {stringOrUndefined} from '@src/utils/strings'
import {useSetState, useUpdateEffect} from 'ahooks'
import {LocationLogEntrySearchFilters} from './components/LocationLogEntrySearchFilters.tsx'
import {EntriesDay} from './components/EntriesDay'
import {IoMdAdd, IoMdDownload} from 'react-icons/io'
import {MdRefresh} from 'react-icons/md'
import {IoDocumentTextSharp} from 'react-icons/io5'

function EntriesTab() {
  const [hoursForRecentEntries, setHoursForRecentEntries] = useLocalStorage(
    'rcnthrs',
    24,
  )
  const recentEntriesOptions = [
    {label: '1 hour', value: 1},
    {label: '2 hours', value: 2},
    {label: '4 hours', value: 4},
    {label: '6 hours', value: 6},
    {label: '8 hours', value: 8},
    {label: '12 hours', value: 12},
    {label: '1 day', value: 24},
    {label: '2 days', value: 48},
    {label: '3 days', value: 72},
    {label: '1 week', value: 168},
    {label: '2 weeks', value: 336},
    {label: '4 weeks', value: 672},
  ]

  const canDownloadAndExportData = useCurrentUserAnPCheck(
    ({anp}) => anp.role.ops.access.canDownloadAndExportData,
  )
  const {client} = useDownloadsApiServiceContext()
  const canCreateLocationLogEntries = useCurrentUserAnPCheck(
    (user) => user.anp.role.ops.permissions.createLocationLogEntries,
  )
  const canDownloadEntries = useCurrentUserAnPCheck(
    (user) => user.anp.role.ops.access.canDownloadAndExportData,
  )
  const {location, locationId} = useLocationContext(true)
  const [searchTextValue, searchText, setSearchText] = useUrlStateDebounced(
    'loclogentrysearch',
    '',
  )

  const [state, setState] = useState<Record<string, boolean | string>>({
    selectedEntryTypeId: false,
    selectedEntryTypeName: '',
    showAddEntryModal: false,
    showDownloadEntriesModal: false,
  })

  const [editEntryId, setEditEntryId] = useState<string>()
  const [downloadEntry, setDownloadEntry] = useState<
    RPCDocs.Locations.GetLocationLogEntryList.v2.Entry | undefined
  >()

  const {recordsPerPage, setRecordsPerPage, setSkippedRecords, skippedRecords} =
    usePaginationState('loclog', [])

  const [filtersState, setFiltersState] = useSetState<{
    activityTypeIds: string[] | undefined
    selectedUserIds: string[] | undefined
    selectedEntryTypeIds: string[] | undefined
    selectedDateTimeRange: {start: undefined; end: undefined}
  }>({
    activityTypeIds: undefined,
    selectedDateTimeRange: {end: undefined, start: undefined},
    selectedEntryTypeIds: undefined,
    selectedUserIds: undefined,
  })

  const [urlState, setUrlState] = useUrlState(
    'loclogentryfilters',
    filtersState,
  )

  useUpdateEffect(() => {
    setSkippedRecords(0)
  }, [filtersState, searchText])

  useEffect(() => {
    if (urlState) {
      setFiltersState(urlState)
    }
  }, [])

  useUpdateEffect(() => {
    setUrlState(filtersState)
  }, [filtersState])

  const filters = {
    activityTypeIds: urlState.activityTypeIds?.length
      ? urlState.activityTypeIds
      : undefined,
    entryTypeIds: urlState.selectedEntryTypeIds?.length
      ? urlState.selectedEntryTypeIds
      : undefined,
    fromDate: urlState.selectedDateTimeRange?.start || undefined,
    toDate: urlState.selectedDateTimeRange?.end || undefined,
    userIds: urlState.selectedUserIds?.length
      ? urlState.selectedUserIds
      : undefined,
  }

  const {
    data: entriesByDateData,
    loading: entriesByDateDataLoading,
    refresh,
  } = useLocationLogEntriesByDate({
    ...filters,
    locationId,
    pagination: {limit: recordsPerPage, skip: skippedRecords},
    searchText,
  })

  const {data: entriesStats} = useRPC<
    RPCDocs.Stats.GetLocationLogEntriesStatsCount.v1.Args,
    RPCDocs.Stats.GetLocationLogEntriesStatsCount.v1.Data
  >(
    RPCs.Stats.GetLocationLogEntriesStatsCount.v1,
    {
      fromDate: dayjs()
        .subtract(hoursForRecentEntries, 'hours')
        .startOf('hour')
        .toISOString(),
      locationAndGroupIds: [locationId],
      toDate: dayjs().endOf('hour').toISOString(),
    },
    {
      cacheStrategy: 'cache-only',
    },
  )

  const handleDownloadLocationLogEntryPDF = (
    entry: RPCDocs.Locations.GetLocationLogEntryList.v2.Entry,
    refOptions: {
      showEventsRefs?: boolean
      showGpsCoords?: boolean
      showInternalContent?: boolean
      showLocationsRefs?: boolean
      showRecordsRefs?: boolean
      showReportsRefs?: boolean
      showUsersRefs?: boolean
      withRefHistory?: boolean
    },
  ) => {
    const toastId = Alerts.info(
      <div>
        <Icon Icon={MdRefresh} spin /> Starting download, please wait...
      </div>,
      {autoClose: false, position: 'top-center'},
    )

    client.locations.DownloadLocationLogEntry(
      {
        body: {
          entryId: entry.id,
          showEventsRefs: refOptions.showEventsRefs,
          showGpsCoords: refOptions.showGpsCoords,
          showInternalContent: refOptions.showInternalContent,
          showLocationsRefs: refOptions.showLocationsRefs,
          showRecordsRefs: refOptions.showRecordsRefs,
          showReportsRefs: refOptions.showReportsRefs,
          showUsersRefs: refOptions.showUsersRefs,
          withRefHistory: refOptions.withRefHistory,
        },
      },
      {timeout: 60_000},
    )
      .then((response) => {
        const result = response as DownloadPdfResult
        if (result.pdfBase64) {
          downloadBase64AsPdf(
            `${location?.name} Log Entry - ${entry.entryType.name} - ${dayjs(
              entry.timestamp,
            ).format()} ${entry.title} ${entry.subTitle}`,
            result.pdfBase64,
          )
        }
      })
      .catch((err: any) => {
        console.error('err downloading entry', err)
      })
      .finally(() => toastManager.dismiss(toastId))
  }

  useRPCServerEventHandler('LocationLogEntryCreated', (evt, data) => {
    if (data.locationId === locationId) {
      refresh()
    }
  })

  useRPCServerEventHandler('LocationLogEntryUpdated', (evt, data) => {
    if (data.locationId === locationId) {
      refresh()
    }
  })

  return (
    <div className="relative">
      <div className="gap-sm flex w-full flex-wrap-reverse items-end justify-between">
        <div className="grow">
          <LocationLogEntrySearchFilters
            filtersState={filtersState}
            setFiltersState={setFiltersState}
            searchText={searchTextValue}
            setSearchText={setSearchText}
          />
        </div>

        {(canCreateLocationLogEntries || canDownloadEntries) && (
          <div className="gap-xxs ml-auto flex">
            {canDownloadEntries && (
              <Button
                onClick={() =>
                  setState((oldState) => ({
                    ...oldState,
                    showDownloadEntriesModal: true,
                  }))
                }
                outline
                variant="main"
                size="sm"
              >
                <Icon Icon={IoMdDownload} />
                Download
              </Button>
            )}

            {location?.active && canCreateLocationLogEntries && (
              <Button
                onClick={() => setState({showAddEntryModal: true})}
                variant="main"
              >
                <Icon Icon={IoMdAdd} size="sm" />
                Log Entry
              </Button>
            )}
          </div>
        )}
      </div>

      <div className="relative">
        {entriesByDateDataLoading && !entriesByDateData?.entriesByDate ? (
          <div className="relative h-20">
            <Loading message="Loading Entries" overlay size="xl" />
          </div>
        ) : (
          <div className="space-y-base">
            {Object.entries(entriesByDateData?.entriesByDate || {})?.map(
              ([date, entries]) => (
                <EntriesDay
                  onEditEntryId={location?.active ? setEditEntryId : undefined}
                  onDownloadPDF={
                    canDownloadAndExportData
                      ? (entry) => setDownloadEntry(entry)
                      : undefined
                  }
                  date={date}
                  entries={entries}
                  key={date}
                />
              ),
            )}

            <div className="my-base">
              <Pagination
                onPageClick={setSkippedRecords}
                onSetRecordsPerPage={setRecordsPerPage}
                recordsPerPage={recordsPerPage}
                showDescription
                skippedRecords={skippedRecords}
                totalRecords={entriesByDateData?.total ?? 0}
              />
            </div>
          </div>
        )}

        {!entriesByDateDataLoading && !entriesByDateData?.total && (
          <div className="flex h-full grow items-center justify-center opacity-50">
            <span>
              <Icon Icon={IoDocumentTextSharp} color="neutral" size="xl" />
            </span>
            <span className="text-center text-lg font-medium capitalize">
              No Entries
            </span>
          </div>
        )}

        {entriesStats?.count !== undefined && (
          <div className="z-sticky bg-background py-xxs sticky right-0 bottom-0 text-right text-xs">
            <span>{entriesStats.count}</span>{' '}
            <span className="text-color-subtle">
              {pluralize(['Entry', 'Entries'], entriesStats.count)} in the
              last{' '}
            </span>
            <select
              className="border-border bg-surface-subtle focus:ring-main ml-xxs pl-xs py-xxs pr-xl cursor-pointer rounded border text-xs focus:ring-1 focus:outline-none"
              onChange={(event) =>
                setHoursForRecentEntries(Number(event.target.value))
              }
              value={hoursForRecentEntries}
            >
              {recentEntriesOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {!!editEntryId && (
        <EntryFormModal
          closeHandler={() => setEditEntryId(undefined)}
          entryId={editEntryId}
          onComplete={() => refresh()}
        />
      )}

      {!!downloadEntry && (
        <PickLocationLogEntryRefsModal
          onDownload={(values) =>
            handleDownloadLocationLogEntryPDF(downloadEntry, values)
          }
          closeHandler={() => setDownloadEntry(undefined)}
        />
      )}

      {state.showAddEntryModal && (
        <SelectLocationLogEntryTypeModal
          closeHandler={() => setState({})}
          entryTypeIds={location?.config?.log?.allowedEntryTypeIds ?? []}
          onSelectEntryType={(id, name) => {
            setState({selectedEntryTypeId: id, selectedEntryTypeName: name})
          }}
        />
      )}

      {state.showDownloadEntriesModal && (
        <DownloadLogEntriesModal
          activityTypeIds={filtersState.activityTypeIds}
          closeHandler={() =>
            setState((oldState) => ({
              ...oldState,
              showDownloadEntriesModal: false,
            }))
          }
          entryTypeIds={filtersState.selectedEntryTypeIds}
          showInternalContent
          showReferences
          userIds={filtersState.selectedUserIds}
        />
      )}

      {state.selectedEntryTypeId && (
        <EntryFormModal
          closeHandler={() => setState({})}
          entryTypeId={stringOrUndefined(state.selectedEntryTypeId)}
          entryTypeName={stringOrUndefined(state.selectedEntryTypeName)}
          onComplete={() => null}
        />
      )}
    </div>
  )
}

export {EntriesTab}
